/**
 * Source of "now". Logic never calls `Date.now()` itself; it receives a clock so tests
 * (and the sales replay, which runs at thousands of times real speed) control time.
 */
export interface Clock {
  /** The current instant. */
  now(): Date;
}

/** The real wall clock. Use only at the edges (app shell, server request handler). */
export const systemClock: Clock = {
  now: () => new Date(),
};

/** A clock frozen at one instant that tests can move by hand. */
export interface FixedClock extends Clock {
  /** Jump to an instant. */
  set(instant: Date | string): void;
  /** Move forward (or back, with a negative value) by `ms` milliseconds. */
  advance(ms: number): void;
}

/** Creates a {@link FixedClock} starting at `instant` (ISO string with offset, or Date). */
export function fixedClock(instant: Date | string): FixedClock {
  let current = toDate(instant);
  return {
    now: () => new Date(current.getTime()),
    set: (next) => {
      current = toDate(next);
    },
    advance: (ms) => {
      if (!Number.isFinite(ms)) throw new RangeError('advance(ms) needs a finite number');
      current = new Date(current.getTime() + ms);
    },
  };
}

// ISO-8601 instant with an explicit offset. A string without Z/offset would be read in the
// device's local zone, which is exactly the midnight bug we are guarding against.
const ISO_INSTANT_RE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:\d{2})$/;

/** True when `text` is an ISO-8601 instant carrying `Z` or a `±HH:mm` offset. */
export function isIsoInstant(text: string): boolean {
  return ISO_INSTANT_RE.test(text) && !Number.isNaN(Date.parse(text));
}

/**
 * Converts an instant (Date, or ISO string with offset) to a Date.
 * Throws on invalid dates and on ISO strings without an offset.
 */
export function toDate(instant: Date | string): Date {
  if (instant instanceof Date) {
    if (Number.isNaN(instant.getTime())) throw new RangeError('Invalid Date');
    return new Date(instant.getTime());
  }
  if (!isIsoInstant(instant)) {
    throw new RangeError(`Not an ISO instant with an offset: ${JSON.stringify(instant)}`);
  }
  return new Date(instant);
}
